import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const PROMPT_PLACEHOLDER = `Describe this agent's identity, communication style, and behavior in natural language.

Examples:
- "A 45-year-old female patient recently diagnosed with anxiety disorder. Speaks hesitantly, frequently loses train of thought mid-sentence, avoids direct eye contact, and becomes defensive when asked about medication compliance."
- "An experienced customer service representative. Speaks professionally and calmly, uses clear concise language, occasionally interrupts when frustrated, and always tries to de-escalate conflict."
- "A 70-year-old male with mild cognitive impairment. Speaks slowly, repeats himself, struggles to recall recent events, and often asks for clarification."`;

const LLM_PROVIDERS = [
  {
    id: 'anthropic', label: 'Anthropic',
    models: [
      { id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
      { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
    ],
  },
  {
    id: 'openai', label: 'OpenAI',
    models: [
      { id: 'gpt-4o', label: 'GPT-4o' },
      { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    ],
  },
  {
    id: 'google', label: 'Google',
    models: [
      { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    ],
  },
];

const styles = {
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '32px',
  },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  backBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '32px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    maxWidth: '720px',
  },
  sectionTitle: {
    fontSize: '14px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '20px',
    paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  label: {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '20px',
    boxSizing: 'border-box', outline: 'none',
  },
  select: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '8px',
    boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff',
  },
  selectNoMargin: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '20px',
    boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff',
  },
  textarea: {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '8px',
    boxSizing: 'border-box', outline: 'none',
    minHeight: '220px', resize: 'vertical', lineHeight: '1.6',
  },
  hint: { fontSize: '12px', color: '#9ca3af', marginBottom: '24px' },
  voiceCard: {
    backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px',
    border: '1px solid #e5e7eb', marginBottom: '20px',
  },
  voiceSelected: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  voiceName: { fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '4px' },
  voiceMeta: { fontSize: '12px', color: '#6b7280', marginBottom: '8px' },
  voiceLabels: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' },
  voiceLabel: {
    fontSize: '10px', fontWeight: '600', color: '#0052CC',
    backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '20px',
  },
  previewBtn: {
    padding: '5px 12px', backgroundColor: '#fff', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer',
  },
  loadingVoices: { fontSize: '13px', color: '#9ca3af', marginBottom: '20px' },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' },
  saveBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '12px 32px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
  },
  cancelBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '12px 24px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
  },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5',
    color: '#dc2626', borderRadius: '8px', padding: '12px 16px',
    fontSize: '13px', marginBottom: '20px',
  },
  success: {
    backgroundColor: '#f0fdf4', border: '1px solid #86efac',
    color: '#16a34a', borderRadius: '8px', padding: '12px 16px',
    fontSize: '13px', marginBottom: '20px',
  },
  llmCard: {
    backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px',
    border: '1px solid #e5e7eb', marginBottom: '20px',
  },
};

export default function AgentBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { currentUser, isPro } = useAuth();

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [voiceName, setVoiceName] = useState('');
  const [llmProvider, setLlmProvider] = useState('anthropic');
  const [llmModel, setLlmModel] = useState('claude-sonnet-4-5');
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVoices();
    if (isEdit) fetchAgent();
  }, [id]);

  async function fetchVoices() {
    try {
      setLoadingVoices(true);
      const res = await fetch(`${API}/api/voices`);
      const data = await res.json();
      setVoices(Array.isArray(data) ? data : []);
    } catch {
      console.error('Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  }

  async function fetchAgent() {
    try {
      const res = await fetch(`${API}/api/agents/${id}`);
      const data = await res.json();
      setName(data.name || '');
      setRole(data.role || '');
      setSystemPrompt(data.system_prompt || '');
      setVoiceId(data.voice_id || '');
      setVoiceName(data.voice_name || '');
      setLlmProvider(data.llm_provider || 'anthropic');
      setLlmModel(data.llm_model || 'claude-sonnet-4-5');
    } catch {
      setError('Failed to load agent.');
    }
  }

  function handleVoiceSelect(e) {
    const selectedId = e.target.value;
    const selectedVoice = voices.find(v => v.voice_id === selectedId);
    setVoiceId(selectedId);
    setVoiceName(selectedVoice?.name || '');
  }

  function handleProviderChange(e) {
    const provider = e.target.value;
    setLlmProvider(provider);
    const firstModel = LLM_PROVIDERS.find(p => p.id === provider)?.models[0]?.id || '';
    setLlmModel(firstModel);
  }

  function playPreview(previewUrl) {
    if (!previewUrl) return;
    const audio = new Audio(previewUrl);
    audio.play();
  }

  const selectedVoice = voices.find(v => v.voice_id === voiceId);
  const currentProviderModels = LLM_PROVIDERS.find(p => p.id === llmProvider)?.models || [];

  async function handleSave() {
    if (!name.trim()) { setError('Agent name is required.'); return; }
    if (!systemPrompt.trim()) { setError('Agent prompt is required.'); return; }
    setLoading(true);
    setError('');
    setSuccess('');

    const payload = {
      owner_id: currentUser.uid,
      name,
      role,
      system_prompt: systemPrompt,
      voice_id: voiceId,
      voice_name: voiceName,
      llm_provider: llmProvider,
      llm_model: llmModel,
    };

    try {
      const url = isEdit ? `${API}/api/agents/${id}` : `${API}/api/agents`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      setSuccess(isEdit ? 'Agent updated.' : 'Agent created.');
      setTimeout(() => navigate('/agents'), 1000);
    } catch {
      setError('Failed to save agent.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{isEdit ? 'Edit Agent' : 'New Agent'}</div>
          <div style={styles.sub}>Define your agent using a natural language prompt</div>
        </div>
        <button style={styles.backBtn} onClick={() => navigate('/agents')}>← Back</button>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Agent Configuration</div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.row}>
          <div>
            <label style={styles.label}>Agent Name *</label>
            <input
              style={styles.input}
              value={name}
              onChange={e => setName(e.target.value.slice(0, 100))}
              placeholder="e.g. Patient A, Clinician, Customer"
              maxLength={100}
            />
            <div style={{ fontSize: '11px', color: name.length > 80 ? '#dc2626' : '#9ca3af', textAlign: 'right' }}>{name.length}/100</div>
          </div>
          <div>
            <label style={styles.label}>Role</label>
            <input
              style={styles.input}
              value={role}
              onChange={e => setRole(e.target.value.slice(0, 100))}
              placeholder="e.g. patient, clinician, support agent"
              maxLength={100}
            />
            <div style={{ fontSize: '11px', color: role.length > 80 ? '#dc2626' : '#9ca3af', textAlign: 'right' }}>{role.length}/100</div>
          </div>
        </div>

        <label style={styles.label}>Agent Prompt *</label>
        <textarea
          style={styles.textarea}
          value={systemPrompt}
          onChange={e => setSystemPrompt(e.target.value.slice(0, 2000))}
          placeholder={PROMPT_PLACEHOLDER}
          maxLength={2000}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={styles.hint}>
            Describe identity, communication style, behavioral tendencies, cognitive traits,
            and any constraints in natural language.
          </div>
          <div style={{ fontSize: '11px', color: systemPrompt.length > 1800 ? '#dc2626' : '#9ca3af', flexShrink: 0, marginLeft: '12px' }}>{systemPrompt.length}/2000</div>
        </div>

        {/* LLM Selection */}
        <label style={styles.label}>LLM (Language Model)</label>
        <div style={styles.llmCard}>
          <div style={styles.row}>
            <div>
              <label style={styles.label}>Provider</label>
              <select style={styles.selectNoMargin} value={llmProvider} onChange={handleProviderChange}>
                {LLM_PROVIDERS.filter(p => isPro || p.id === 'anthropic').map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              {!isPro && (
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  Claude only on Standard plan. <a href="mailto:info@converstation.ai" style={{ color: '#0052CC' }}>Upgrade to Professional</a> for GPT-4o and Gemini.
                </div>
              )}
            </div>
            <div>
              <label style={styles.label}>Model</label>
              <select
                style={styles.selectNoMargin}
                value={llmModel}
                onChange={e => setLlmModel(e.target.value)}
              >
                {currentProviderModels.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Voice Selection */}
        <label style={styles.label}>Voice (ElevenLabs)</label>
        {loadingVoices ? (
          <div style={styles.loadingVoices}>Loading voices...</div>
        ) : (
          <>
            <select style={styles.select} value={voiceId} onChange={handleVoiceSelect}>
              <option value="">Select a voice...</option>
              {voices.map(v => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name}
                  {v.labels?.gender ? ` · ${v.labels.gender}` : ''}
                  {v.labels?.accent ? ` · ${v.labels.accent}` : ''}
                </option>
              ))}
            </select>

            {selectedVoice && (
              <div style={{ ...styles.voiceCard, ...styles.voiceSelected }}>
                <div style={styles.voiceName}>{selectedVoice.name}</div>
                {selectedVoice.description && (
                  <div style={styles.voiceMeta}>{selectedVoice.description}</div>
                )}
                <div style={styles.voiceLabels}>
                  {Object.entries(selectedVoice.labels || {}).map(([key, val]) => (
                    <span key={key} style={styles.voiceLabel}>
                      {key}: {val}
                    </span>
                  ))}
                </div>
                {selectedVoice.preview_url && (
                  <button
                    style={styles.previewBtn}
                    onClick={() => playPreview(selectedVoice.preview_url)}
                  >
                    ▶ Preview Voice
                  </button>
                )}
              </div>
            )}
          </>
        )}

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={() => navigate('/agents')}>Cancel</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Agent' : 'Create Agent'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
